import { styled } from "goober";
import { memo } from "react";
import type { ConnectorConfig } from "../lib/connectorDirectory";
import { ConnectorLogoContainer } from "./ConnectorLogoContainer";
import { Link } from "./Link";
import { Text } from "./Text";

const LogoScale = styled("div")`
  display: flex;
  flex-shrink: 0;
  transition: transform 0.1s ease-out;
`;

const StyledLink = styled(Link)`
  all: unset;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  border: 0.5px solid var(--border-alpha);
  cursor: pointer;
  /* The directory renders ~1,000 cards; let the browser skip layout and paint
     of offscreen ones. The intrinsic size matches the card's rendered box so
     scroll height stays stable while skipped. */
  content-visibility: auto;
  contain-intrinsic-size: auto 216px auto 58px;

  &:hover {
    background: var(--surface-hover);
  }

  &:hover ${LogoScale} {
    transform: scale(1.08);
  }
`;

const Details = styled("div")`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const TitleRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CustomBadge = styled("span")`
  display: inline-flex;
  align-items: center;
  height: 16px;
  padding: 0 6px;
  border-radius: 8px;
  background: var(--green-200);
  color: var(--green-800);
`;

type Props = {
  app: ConnectorConfig;
  to: string;
  disabled?: boolean;
};

export const ConnectorOption = memo(function ConnectorOption({
  app,
  to,
  disabled,
}: Props) {
  const isCustom = app.tags.includes("custom");

  return (
    <StyledLink
      to={to}
      style={disabled ? { opacity: 0.5, pointerEvents: "none" } : undefined}
    >
      <LogoScale>
        <ConnectorLogoContainer
          size={32}
          borderRadius={8}
          outline={false}
          hoverBackground={false}
        >
          <img
            alt={`${app.name} logo`}
            src={app.definition.logo}
            loading="lazy"
            decoding="async"
            style={{ width: 16, height: 16, borderRadius: 4, flexShrink: 0 }}
          />
        </ConnectorLogoContainer>
      </LogoScale>
      <Details>
        <TitleRow>
          <Text variant="text-regular-md" color="var(--text-primary)" ellipsis>
            {app.name}
          </Text>
          {isCustom && (
            <CustomBadge>
              <Text variant="text-smaller-md">Custom</Text>
            </CustomBadge>
          )}
        </TitleRow>
        <Text variant="text-small" color="var(--text-secondary)" ellipsis>
          {app.tagLine}
        </Text>
      </Details>
    </StyledLink>
  );
});
